import React, { Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';

import { useAuth } from './contexts/AuthContext';
import { PiSDKProvider } from './contexts/PiSDKContext';
import { LanguageProvider } from './contexts/LanguageContext';
import ProtectedRoute from './components/ProtectedRoute';
import LoadingSpinner from './components/LoadingSpinner';

// Lazy load components for better performance
const Layout = React.lazy(() => import('./components/Layout/Layout'));
const Home = React.lazy(() => import('./pages/Home'));
const Login = React.lazy(() => import('./pages/Login'));
const About = React.lazy(() => import('./pages/About'));
const FAQ = React.lazy(() => import('./pages/FAQ'));
const HelpCenter = React.lazy(() => import('./pages/HelpCenter'));
const PrivacyPolicy = React.lazy(() => import('./pages/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./pages/TermsOfService'));
const SecurityPolicy = React.lazy(() => import('./pages/SecurityPolicy'));
const CookiePolicy = React.lazy(() => import('./pages/CookiePolicy'));
const SecurityDashboard = React.lazy(() => import('./pages/SecurityDashboard'));
const Dashboard = React.lazy(() => import('./pages/Dashboard'));
const Profile = React.lazy(() => import('./pages/Profile'));
const TopUp = React.lazy(() => import('./pages/TopUp'));
const GiftCards = React.lazy(() => import('./pages/GiftCards'));
const BillPayments = React.lazy(() => import('./pages/BillPayments'));
const Wallet = React.lazy(() => import('./pages/Wallet'));
const SmartWallet = React.lazy(() => import('./pages/SmartWallet'));
const Admin = React.lazy(() => import('./pages/Admin'));

// Error boundary for lazy loaded components
class LazyLoadErrorBoundary extends React.Component<
  { children: React.ReactNode },
  { hasError: boolean }
> {
  constructor(props: { children: React.ReactNode }) {
    super(props);
    this.state = { hasError: false };
  }

  static getDerivedStateFromError() {
    return { hasError: true };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    console.error('Lazy load error:', error, errorInfo);
  }

  render() {
    if (this.state.hasError) {
      return (
        <div style={{ 
          display: 'flex', 
          justifyContent: 'center', 
          alignItems: 'center', 
          height: '100vh',
          flexDirection: 'column'
        }}>
          <h2>Something went wrong loading this page</h2>
          <button onClick={() => window.location.reload()}>
            Reload Page
          </button>
        </div>
      );
    }

    return this.props.children;
  }
}

function App() {
  const { user, loading } = useAuth();

  if (loading) {
    return <LoadingSpinner />;
  }

  return (
    <LanguageProvider>
      <PiSDKProvider>
        <LazyLoadErrorBoundary>
          <div className="App">
            <Suspense fallback={<LoadingSpinner message="Loading application..." />}>
              <Routes>
              {/* Public Routes - Always accessible */}
              <Route 
                path="/" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading..." />}>
                    <Home />
                  </Suspense>
                } 
              />
              <Route 
                path="/login" 
                element={
                  !user ? (
                    <Suspense fallback={<LoadingSpinner message="Loading login..." />}>
                      <Login />
                    </Suspense>
                  ) : (
                    <Navigate to="/dashboard" />
                  )
                } 
              />
              <Route 
                path="/about" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading about page..." />}>
                    <About />
                  </Suspense>
                } 
              />
              <Route 
                path="/faq" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading FAQ..." />}>
                    <FAQ />
                  </Suspense>
                } 
              />
              <Route 
                path="/help" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading Help Center..." />}>
                    <HelpCenter />
                  </Suspense>
                } 
              />
              <Route 
                path="/privacy" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading Privacy Policy..." />}>
                    <PrivacyPolicy />
                  </Suspense>
                } 
              />
              <Route 
                path="/terms" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading Terms of Service..." />}>
                    <TermsOfService />
                  </Suspense>
                } 
              />
              <Route 
                path="/security-policy" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading Security Policy..." />}>
                    <SecurityPolicy />
                  </Suspense>
                } 
              />
              <Route 
                path="/cookies" 
                element={
                  <Suspense fallback={<LoadingSpinner message="Loading Cookie Policy..." />}>
                    <CookiePolicy />
                  </Suspense>
                } 
              />
              
              {/* Protected Routes */}
              <Route path="/app" element={
                <ProtectedRoute>
                  <Suspense fallback={<LoadingSpinner message="Loading dashboard..." />}>
                    <Layout />
                  </Suspense>
                </ProtectedRoute>
              }>
                <Route index element={<Navigate to="/app/dashboard" />} />
                <Route 
                  path="dashboard" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading dashboard..." />}>
                      <Dashboard />
                    </Suspense>
                  } 
                />
                <Route 
                  path="profile" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading profile..." />}>
                      <Profile />
                    </Suspense>
                  } 
                />
                <Route 
                  path="topup" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading top-up..." />}>
                      <TopUp />
                    </Suspense>
                  } 
                />
                <Route 
                  path="giftcards" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading gift cards..." />}>
                      <GiftCards />
                    </Suspense>
                  } 
                />
                <Route 
                  path="bill-payments" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading bill payments..." />}>
                      <BillPayments />
                    </Suspense>
                  } 
                />
                <Route 
                  path="wallet" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading wallet..." />}>
                      <Wallet />
                    </Suspense>
                  } 
                />
                <Route 
                  path="smart-wallet" 
                  element={
                    <Suspense fallback={<LoadingSpinner message="Loading smart wallet..." />}>
                      <SmartWallet />
                    </Suspense>
                  } 
                />
                <Route 
                  path="security" 
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Suspense fallback={<LoadingSpinner message="Loading security dashboard..." />}>
                        <SecurityDashboard />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
                <Route 
                  path="admin" 
                  element={
                    <ProtectedRoute requiredRole="admin">
                      <Suspense fallback={<LoadingSpinner message="Loading admin panel..." />}>
                        <Admin />
                      </Suspense>
                    </ProtectedRoute>
                  } 
                />
              </Route>
              
              {/* Legacy route redirects for backward compatibility */}
              <Route path="/dashboard" element={<Navigate to="/app/dashboard" />} />
              <Route path="/profile" element={<Navigate to="/app/profile" />} />
              <Route path="/topup" element={<Navigate to="/app/topup" />} />
              <Route path="/giftcards" element={<Navigate to="/app/giftcards" />} />
              <Route path="/bill-payments" element={<Navigate to="/app/bill-payments" />} />
              <Route path="/wallet" element={<Navigate to="/app/wallet" />} />
              <Route path="/smart-wallet" element={<Navigate to="/app/smart-wallet" />} />
              <Route path="/admin" element={<Navigate to="/app/admin" />} />
              
              <Route path="*" element={<Navigate to={user ? "/app/dashboard" : "/"} />} />
            </Routes>
          </Suspense>
        </div>
      </LazyLoadErrorBoundary>
    </PiSDKProvider>
    </LanguageProvider>
  );
}

export default App;